/* Root layout: sidebar on desktop, hamburger sheet on mobile. */

import { lazy, Suspense, useState } from "react";
import { Outlet } from "react-router-dom";
import { AppSidebar } from "@/components/layout/AppSidebar";
import { MobileHeader } from "@/components/layout/MobileHeader";
import { ScrollToTop } from "@/components/layout/ScrollToTop";
import { AnnouncementBanner } from "@/components/announcements/AnnouncementBanner";
import { useColorTheme } from "@/hooks/useColorTheme";
import { useActiveKiwiSkin } from "@/hooks/useActiveKiwiSkin";
import { useKiwiThemeSync } from "@/hooks/useKiwiThemeSync";
import { GuidedWalkthroughProvider } from "@/contexts/GuidedWalkthroughProvider";
import { GuidedWalkthroughOverlay } from "@/components/walkthrough/GuidedWalkthroughOverlay";
import { useAudioPlayer } from "@/hooks/useAudioPlayer";
import { useVideoPlayer } from "@/hooks/useVideoPlayer";
import { MiniPlayer } from "@/components/player/MiniPlayer";
import { PostHogUserProperties } from "@/components/auth/PostHogUserProperties";

const KiwiChatWidget = lazy(() =>
  import("@/components/chat/KiwiChatWidget").then((m) => ({ default: m.KiwiChatWidget })),
);

const ExpandedPlayer = lazy(() =>
  import("@/components/player/ExpandedPlayer").then((m) => ({ default: m.ExpandedPlayer })),
);

const YouTubeViewer = lazy(() =>
  import("@/components/player/YouTubeViewer").then((m) => ({ default: m.YouTubeViewer })),
);

export function AppLayout() {
  const { colorTheme } = useColorTheme();
  const { activeSkin } = useActiveKiwiSkin();
  useKiwiThemeSync(colorTheme, activeSkin);
  const { state } = useAudioPlayer();
  const videoPlayer = useVideoPlayer();
  const [isExpanded, setIsExpanded] = useState(false);
  const hasActiveTrack = state.track !== null;

  return (
    <GuidedWalkthroughProvider>
      <PostHogUserProperties />
      <div className="min-h-screen bg-background">
        <ScrollToTop />
        <MobileHeader />

        <div className="mx-auto w-full max-w-[1200px] md:px-6">
          <div className="flex min-w-0">
            <div className="hidden lg:block w-64 shrink-0">
              <div className="fixed top-6 bottom-6 w-64">
                <AppSidebar className="h-full rounded-lg border border-sidebar-border" />
              </div>
            </div>

            <main className={`relative min-w-0 flex-1 flex flex-col ${hasActiveTrack ? "pb-24" : "pb-8"}`}>
              <div className="flex-1 min-h-0">
                <Outlet />
              </div>
            </main>
          </div>
        </div>

        <MiniPlayer onExpand={() => setIsExpanded(true)} />

        <Suspense fallback={null}>
          <ExpandedPlayer open={isExpanded} onClose={() => setIsExpanded(false)} />
        </Suspense>

        <Suspense fallback={null}>
          {videoPlayer.state.track && <YouTubeViewer />}
        </Suspense>

        <AnnouncementBanner />
        <Suspense fallback={null}>
          <KiwiChatWidget hideForExpandedContent={isExpanded || videoPlayer.state.track !== null} />
        </Suspense>
        <GuidedWalkthroughOverlay />
      </div>
    </GuidedWalkthroughProvider>
  );
}
